/**
 * Returns a copy of a selection with a group of ids selected or deselected.
 *
 * Selecting adds the ids that are not selected yet, keeping the existing order and never duplicating an id.
 * Deselecting removes only the given ids, so anything selected outside the group stays selected.
 * @param {Array} selectedIds - The ids selected so far.
 * @param {Array} ids - The ids to select or deselect.
 * @param {boolean} isSelected - True to select the ids, false to deselect them.
 * @return {Array} The new selection. A new array, the inputs are left untouched.
 */
const withIdsSelected = (selectedIds, ids, isSelected) => {
  const targetIds = new Set(ids);
  if (isSelected) {
    const alreadySelected = new Set(selectedIds);
    return [...selectedIds, ...[...targetIds].filter((id) => !alreadySelected.has(id))];
  }
  return selectedIds.filter((id) => !targetIds.has(id));
};

/**
 * How much of a group of ids is selected, in the shape a "select all" checkbox takes.
 *
 * An empty group is never checked, so a "select all" over nothing reads as unchecked.
 * @param {Array} ids - The ids in the group, e.g. the rows on the current table page.
 * @param {Array} selectedIds - The ids selected overall, which may include ids outside the group.
 * @return {{checked: boolean, indeterminate: boolean}} Checked when every id in the group is selected,
 *   indeterminate when some but not all are, both false otherwise.
 */
const getSelectionState = (ids, selectedIds) => {
  const selected = new Set(selectedIds);
  const selectedCount = ids.filter((id) => selected.has(id)).length;
  return {
    checked: ids.length > 0 && selectedCount === ids.length,
    indeterminate: selectedCount > 0 && selectedCount < ids.length,
  };
};

export {
  withIdsSelected,
  getSelectionState,
};
